import { useState, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react'

import imgCorzo from '../assets/8b43135dbc0c9e16e1496fc50bb35c1676f2b40d.png'
import imgJabali from '../assets/35ee85eb15ac73963fb750c112a09261c36913b2.png'
import imgCabraMontes from '../assets/93300cd283de98250983c5cb1c9d7f0c35be5f26.png'
import imgCiervo from '../assets/8c8152eb2021b80f4880dee496903dade728006a.png'
import imgPalomaTorcaz from '../assets/d927dbbe0e85a2418fdc50ab89ee52120f52f9ce.png'
import imgMuflon from '../assets/d927dbbe0e85a2967834hdbaokcui37889ee52120f52f9ce.png'
import imgGamo from '../assets/d927dbbe0e85a2418fdc50ab89578120f52f9de.png'
import imgRebeco from '../assets/d927dbbe0e832k23b95n3lab89ee52120f52f9ce.png'
import imgTortola from '../assets/d927dbbe0e832349u8d95n3lab89ee52120f52f9ce.png'
import imgCodorniz from '../assets/img/especies/Codorniz.png'

const ICON_MAP = {
  'Corzo': imgCorzo,
  'Jabalí': imgJabali,
  'Cabra montés': imgCabraMontes,
  'Ciervo': imgCiervo,
  'Paloma torcaz': imgPalomaTorcaz,
  'Muflón': imgMuflon,
  'Rebeco': imgRebeco,
  'Gamo': imgGamo,
  'Tórtola europea': imgTortola,
  'Codorniz': imgCodorniz,
}

const CAZA_MAYOR_NAMES = ['Ciervo', 'Corzo', 'Cabra montés', 'Gamo', 'Muflón', 'Rebeco', 'Jabalí']
const CAZA_MENOR_KEYWORDS = [
  'Paloma', 'Anátidas', 'Zorzal', 'Córvidos', 'Codorniz', 'Perdiz', 'Liebre', 'Conejo', 'Zorro',
  'Becada', 'Agachadiza', 'Avefría', 'Estornino pinto', 'Faisán', 'Focha común', 'Bravía', 'Zurita',
  'Anade', 'Ansar', 'Cerceta', 'Cuchara', 'Corneja', 'Urraca', 'Tórtola',
]
const MAX_SPECIES = 10

function getIcon(nombre) {
  if (!nombre) return null
  if (ICON_MAP[nombre]) return ICON_MAP[nombre]
  // Fallback: buscar por inclusión
  const match = Object.keys(ICON_MAP).find(k => nombre.toLowerCase().includes(k.toLowerCase()))
  return match ? ICON_MAP[match] : null
}

function classify(especie) {
  const n = (especie?.nombre || '').toLowerCase()
  if (CAZA_MAYOR_NAMES.some(name => n.includes(name.toLowerCase()))) return 'mayor'
  if (CAZA_MENOR_KEYWORDS.some(name => n.includes(name.toLowerCase()))) return 'menor'
  return 'menor'
}

const SpeciesSettings = forwardRef(({ especies, visibleSpecies, onSpeciesChange }, ref) => {
  const [tempVisibleSpecies, setTempVisibleSpecies] = useState(() => {
    if (visibleSpecies && Object.keys(visibleSpecies).length > 0) return visibleSpecies
    const initial = {}
    ;(especies || []).forEach(e => { initial[e.id] = false })
    return initial
  })
  useEffect(() => {
    if (visibleSpecies && Object.keys(visibleSpecies).length > 0) {
      setTempVisibleSpecies(visibleSpecies)
    } else if (especies && especies.length > 0) {
      const initial = {}
      especies.forEach(e => { initial[e.id] = false })
      setTempVisibleSpecies(initial)
    }
  }, [especies, visibleSpecies])

  useImperativeHandle(ref, () => ({
    apply: () => onSpeciesChange(tempVisibleSpecies),
  }))

  const selectedCount = useMemo(
    () => (especies || []).filter(e => tempVisibleSpecies[e?.id]).length,
    [especies, tempVisibleSpecies],
  )

  const grupos = useMemo(() => {
    const mayor = []
    const menor = []
    ;(especies || []).forEach(e => {
      if (!e || !e.id) return
      if (classify(e) === 'mayor') mayor.push(e)
      else menor.push(e)
    })
    const byName = (a, b) => (a.nombre || '').localeCompare(b.nombre || '')
    return { mayor: mayor.sort(byName), menor: menor.sort(byName) }
  }, [especies])

  const toggle = (id) => {
    const current = !!tempVisibleSpecies[id]
    if (!current && selectedCount >= MAX_SPECIES) return
    setTempVisibleSpecies(prev => ({ ...prev, [id]: !current }))
  }

  const setGroup = (huntingType, value) => {
    const list = huntingType === 'mayor' ? grupos.mayor : grupos.menor
    setTempVisibleSpecies(prev => {
      const next = { ...prev }
      let count = (especies || []).filter(e => next[e?.id]).length
      list.forEach(e => {
        const wasOn = !!next[e.id]
        if (value && !wasOn) {
          if (count >= MAX_SPECIES) return
          next[e.id] = true
          count++
        } else if (!value && wasOn) {
          next[e.id] = false
          count--
        }
      })
      return next
    })
  }

  const renderGroup = (huntingType, label) => {
    const list = huntingType === 'mayor' ? grupos.mayor : grupos.menor
    if (list.length === 0) return null
    const seleccionadosEnGrupo = list.filter(e => tempVisibleSpecies[e.id]).length
    const todosSeleccionados = seleccionadosEnGrupo === list.length

    return (
      <div className="species-group" key={huntingType}>
        <div className="species-group-header">
          <span className="species-group-title">
            {label}
            <span className="species-group-count">{seleccionadosEnGrupo}/{list.length}</span>
          </span>
          <button
            type="button"
            className="species-group-action"
            onClick={() => setGroup(huntingType, !todosSeleccionados)}
          >
            {todosSeleccionados ? 'Quitar todos' : 'Seleccionar todos'}
          </button>
        </div>
        <div className="species-grid">
          {list.map(e => {
            const isSelected = !!tempVisibleSpecies[e.id]
            const disabled = !isSelected && selectedCount >= MAX_SPECIES
            const icon = getIcon(e.nombre)
            return (
              <button
                type="button"
                key={e.id}
                onClick={() => toggle(e.id)}
                disabled={disabled}
                className={`species-chip${isSelected ? ' species-chip--selected' : ''}${disabled ? ' species-chip--disabled' : ''}`}
                title={disabled ? `Máximo ${MAX_SPECIES} especies` : e.nombre}
              >
                {icon ? (
                  <img src={icon} alt="" className="species-chip-icon" />
                ) : (
                  <span className="species-chip-icon species-chip-icon-fallback" />
                )}
                <span className="species-chip-name">{e.nombre}</span>
                {isSelected && (
                  <span className="species-chip-check" aria-hidden="true">✓</span>
                )}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="species-settings">
      <style>{`
        .species-settings { display:flex; flex-direction:column; gap:14px; min-width:520px; max-width:680px; }
        .species-settings-header { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; }
        .species-count-pill { background:#e6f4ef; color:#0f5e52; font-weight:600; font-size:13px; padding:4px 10px; border-radius:14px; }
        .species-count-pill--max { background:#fff7e6; color:#92400e; }
        .species-search { width:100%; padding:9px 12px; border:1px solid #d1d9e0; border-radius:8px; font-size:14px; outline:none; transition:border-color .15s; }
        .species-search:focus { border-color:#2D6A4F; }
        .species-clear-all { background:none; border:none; color:#6b7280; cursor:pointer; font-size:13px; padding:4px 8px; }
        .species-clear-all:hover { color:#dc2626; text-decoration:underline; }
        .species-groups { display:flex; flex-direction:column; gap:18px; max-height:50vh; overflow-y:auto; padding-right:4px; }
        .species-group-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; }
        .species-group-title { font-size:13px; font-weight:600; color:#374151; text-transform:uppercase; letter-spacing:.3px; }
        .species-group-count { background:#f0f4f8; color:#4a5568; font-weight:500; font-size:12px; padding:2px 8px; border-radius:10px; margin-left:8px; text-transform:none; letter-spacing:0; }
        .species-group-action { background:none; border:none; color:#2D6A4F; font-size:12px; font-weight:600; cursor:pointer; padding:4px 8px; border-radius:6px; }
        .species-group-action:hover { background:#e6f4ef; }
        .species-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(140px, 1fr)); gap:8px; }
        .species-chip { display:flex; align-items:center; gap:8px; padding:8px 10px; background:#fff; border:1.5px solid #e5e7eb; border-radius:10px; cursor:pointer; transition:all .15s; font-size:13px; color:#374151; text-align:left; position:relative; }
        .species-chip:hover:not(.species-chip--disabled) { border-color:#2D6A4F; background:#f9fafb; }
        .species-chip--selected { border-color:#2D6A4F; background:#e6f4ef; color:#0f5e52; font-weight:600; }
        .species-chip--disabled { opacity:.4; cursor:not-allowed; }
        .species-chip-icon { width:28px; height:28px; object-fit:contain; flex-shrink:0; }
        .species-chip-icon-fallback { display:inline-block; background:#f0f4f8; border-radius:50%; }
        .species-chip-name { flex:1; line-height:1.2; word-break:break-word; }
        .species-chip-check { color:#2D6A4F; font-weight:700; font-size:14px; }
        .species-empty { text-align:center; color:#6b7280; font-size:13px; padding:20px; }
      `}</style>

      <div className="species-groups">
        {grupos.mayor.length === 0 && grupos.menor.length === 0 ? (
          <div className="species-empty">No hay especies que coincidan con la búsqueda.</div>
        ) : (
          <>
            {renderGroup('mayor', 'Caza mayor')}
            {renderGroup('menor', 'Caza menor')}
          </>
        )}
      </div>
    </div>
  )
})

SpeciesSettings.displayName = 'SpeciesSettings'

export default SpeciesSettings
